import { useEffect, useState } from "react";
import { CheckCircle2, Shield, ClipboardList, FileText, Download, ArrowRight, CalendarDays, GitBranch, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatShort, daysUntil, formatApiError } from "../lib/format";
import ProcessTimeline from "../components/ProcessTimeline";

export default function ClientPortal() {
  const [data, setData] = useState({ stats: {}, events: [], weapons: [] });
  const [processes, setProcesses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [timelineId, setTimelineId] = useState(null);
  const [user, setUser] = useState(null);

  const load = async () => {
    try {
      const [d, p, docs, me] = await Promise.all([
        api.get("/dashboard"), api.get("/processes"), api.get("/documents"), api.get("/auth/me"),
      ]);
      setData(d.data); setProcesses(p.data); setDocuments(docs.data); setUser(me.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    if (!user?.linked_client_id) { toast.error("Cadastro não vinculado"); return; }
    try {
      const res = await api.get(`/clients/${user.linked_client_id}/portfolio.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `carteira_${(user.name || "cliente").replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Carteira em PDF gerada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="portal-banner">
        <div>
          <span className="eyebrow">PORTAL DO CLIENTE</span>
          <h1>Olá.</h1>
          <p>Acompanhe suas armas, processos e documentos em um só lugar.</p>
        </div>
        <div className="portal-actions">
          <button data-testid="portal-pdf-button" className="ghost-button" onClick={downloadPdf}>
            <FileDown size={15}/> Baixar carteira em PDF
          </button>
          <div className="portal-seal">
            <CheckCircle2 size={24}/>
            <span>CADASTRO<br/><b>REGULAR</b></span>
          </div>
        </div>
      </div>

      <div className="stat-grid portal-stats">
        <div className="stat" data-testid="portal-weapon-stat">
          <div className="stat-icon green"><Shield size={20}/></div>
          <div>
            <span>Minhas armas</span>
            <strong>{data.stats?.weapons ?? 0}</strong>
            <small className="green">Todas monitoradas</small>
          </div>
        </div>
        <div className="stat" data-testid="portal-processes-stat">
          <div className="stat-icon blue"><ClipboardList size={20}/></div>
          <div>
            <span>Processos ativos</span>
            <strong>{data.stats?.open_processes ?? 0}</strong>
            <small className="blue">Em acompanhamento</small>
          </div>
        </div>
        <div className="stat" data-testid="portal-attention-stat">
          <div className="stat-icon red"><CalendarDays size={20}/></div>
          <div>
            <span>Próximas ações</span>
            <strong>{data.stats?.attention ?? 0}</strong>
            <small className="red">Nos próximos 60 dias</small>
          </div>
        </div>
      </div>

      <section className="panel portal-process">
        <div className="panel-head">
          <div><span className="eyebrow">ACOMPANHAMENTO</span><h2>Próximas movimentações</h2></div>
        </div>
        {data.events?.slice(0, 6).map(e => (
          <div className="event-row portal-event" data-testid={`portal-event-${e.id}`} key={e.id}>
            <div className={`event-dot ${(e.type||"").toLowerCase()}`}/>
            <div className="event-info"><b>{e.title}</b><span>{e.detail || "—"}</span></div>
            <time>{formatShort(e.date)}</time>
          </div>
        ))}
        {!data.events?.length && <div className="event-empty">Nenhuma movimentação nos próximos dias.</div>}
      </section>

      <section className="panel">
        <div className="panel-head"><div><span className="eyebrow">MEU ACERVO</span><h2>Minhas armas</h2></div></div>
        <div className="weapon-grid">
          {data.weapons?.map(w => {
            const d = daysUntil(w.craf_expires);
            const label = d === null ? "Sem CRAF" : d < 0 ? "Vencido" : d <= 30 ? "Renovar" : d <= 90 ? "Atenção" : "Regular";
            return (
              <div key={w.id} className="weapon-card">
                <div className="weapon-top"><Shield size={20}/><label className={`status ${d!==null && d<=30 ? "warning-status" : ""}`}>{label}</label></div>
                <h3>{w.model}</h3>
                <p>{w.kind} · {w.caliber}</p>
                <div className="weapon-meta">
                  <span><small>REGISTRO</small>{w.registration || "—"}</span>
                  <span><small>CALIBRE</small>{w.caliber || "—"}</span>
                </div>
                <div className="weapon-footer">
                  <span>Validade CRAF</span>
                  <b>{w.craf_expires ? formatShort(w.craf_expires) : "—"}</b>
                </div>
              </div>
            );
          })}
          {!data.weapons?.length && <div className="empty-inline" style={{gridColumn:"1 / -1"}}>Nenhuma arma vinculada ainda.</div>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head"><div><span className="eyebrow">PROCESSOS</span><h2>Situação dos meus processos</h2></div></div>
        {processes.map(p => (
          <div className="process-row clickable" key={p.id} data-testid={`portal-process-${p.id}`} onClick={()=>setTimelineId(p.id)}>
            <div className="process-icon"><ClipboardList size={18}/></div>
            <div className="process-main">
              <b>{p.kind}</b>
              <span>Prazo {p.due_date ? formatShort(p.due_date) : "—"} · Toque para ver a timeline</span>
              <div className="progress"><i style={{width:`${p.progress||0}%`}} className="blue"/></div>
            </div>
            <strong>{p.progress||0}%</strong>
            <label className="process-status blue">{p.status}</label>
            <GitBranch size={16} style={{color:"#8195ad"}}/>
          </div>
        ))}
        {!processes.length && <div className="empty-inline">Nenhum processo em andamento.</div>}
      </section>

      <section className="panel">
        <div className="panel-head"><div><span className="eyebrow">ARQUIVOS</span><h2>Documentos disponíveis</h2></div></div>
        <div className="doc-list">
          {documents.map(d => (
            <div className="doc-row" key={d.id}>
              <div className="doc-icon"><FileText size={18}/></div>
              <div className="doc-info">
                <b>{d.filename}</b>
                <span>{d.kind} {d.expires_at && `· vence ${formatShort(d.expires_at)}`}</span>
              </div>
              <button data-testid={`portal-download-${d.id}`} className="icon-mini" onClick={()=>download(d)}><Download size={14}/></button>
            </div>
          ))}
          {!documents.length && <div className="empty-inline">Nenhum documento disponível.</div>}
        </div>
      </section>

      {timelineId && (
        <ProcessTimeline processId={timelineId} readOnly onClose={()=>setTimelineId(null)}/>
      )}
    </>
  );
}
